/**
 * Helpers to sort out what sort of media we're dealing with.
 */

export * from './styling';

export type IframeSize = 'auto' | 'large' | 'medium' | 'small';

export const isImage = (type: string) => type.startsWith('image/');
export const isAudio = (type: string) => type.startsWith('audio/');
export const isPDF = (type: string) => type.endsWith('/pdf');
export const isHls = (type: string, url?: string) =>
  type === 'application/x-mpegURL' || url?.endsWith('.m3u8');
export const isVideo = (type: string) => isHls(type) || type.startsWith('video/');
export const isVimeo = (type: string) => type === 'video/vimeo';
export const isUnknownFile = (type: string) => !isImage(type) && !isVideo(type) && !isAudio(type);

/**
 * Accepts a bare video ID, any vimeo.com or player.vimeo.com address
 * (including the vimeo.com/manage/videos/ID page and channel/showcase
 * links), or Vimeo's copied embed code.
 */
export function parseVimeoUrl(input: string): { id: string; hash?: string } | undefined {
  const trimmed = input.trim();

  if (/^\d+$/.test(trimmed)) {
    return { id: trimmed };
  }

  // Embed code: pull the player address out of the iframe's src.
  const embedSrc = trimmed.match(/src=["']([^"']+)["']/i);
  const candidate = (embedSrc ? embedSrc[1] : trimmed).replace(/&amp;/g, '&');

  try {
    const url = new URL(/^https?:\/\//i.test(candidate) ? candidate : `https://${candidate}`);
    const host = url.hostname.toLowerCase();

    if (host === 'player.vimeo.com') {
      const match = url.pathname.match(/^\/video\/(\d+)/);
      if (match) {
        const hash = url.searchParams.get('h') || undefined;
        return { id: match[1], hash };
      }
    }

    if (host === 'vimeo.com' || host === 'www.vimeo.com') {
      // The video ID is the last all-digit path segment, optionally followed
      // by an unlisted hash (hex): /123, /123/abc123def0, /manage/videos/123,
      // /manage/videos/123/privacy, /channels/staff/123, /showcase/9/video/123.
      const segments = url.pathname.split('/').filter(Boolean);
      for (let i = segments.length - 1; i >= 0; i--) {
        if (/^\d+$/.test(segments[i])) {
          const next = segments[i + 1];
          const hash =
            url.searchParams.get('h') ||
            (next && /^[0-9a-f]+$/i.test(next) && !/^\d+$/.test(next) ? next : undefined);
          return { id: segments[i], hash: hash || undefined };
        }
      }
    }
  } catch {
    // Not a valid URL
  }

  return undefined;
}

/**
 * We can't pull in extname from path, since this is shared w/ the browser.
 */
const extname = (path: string) => {
  const i = path.lastIndexOf('.');
  return path.slice(i);
};

/**
 * Given the specified file info, generate the files url.
 */
export const virtualFileUrl = (id: UUID, path: string) => `/files/${id}${extname(path)}`;

/**
 * Matches the id in a `virtualFileUrl`, whether the URL is relative or absolute.
 * Strict 8-4-4-4-12: anything looser can capture a string a student simply typed
 * and hand it to a `uuid` column, which throws rather than returning no rows.
 */
export const FILE_URL_ID_PATTERN =
  /\/files\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

/** Every file id in a blob of content, lowercased the way Postgres stores them. */
export function extractFileIds(content: string) {
  const matches = content.matchAll(new RegExp(FILE_URL_ID_PATTERN, 'gi'));
  return [...matches].map((m) => m[1].toLowerCase());
}

export const stripSVG = (svg: string) => {
  const re = /<script|\/script/gim;
  const matches = svg.match(re);
  if (!matches?.length) {
    return svg;
  }
  const first = matches[0];
  const last = matches[matches.length - 1];
  const prefix = svg.slice(0, svg.indexOf(first));
  const suffix = svg.slice(svg.lastIndexOf(last) + last.length);
  return prefix + suffix.slice(suffix.indexOf('>') + 1);
};
